import AsAnalyticsV from './AsAnalytics.v';
import { withRouter } from 'core/withRouter';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import PageUtil from 'utils/Page.util';
import moment from 'moment';
import { ReceptionDTO, ReceptionSearchDTO } from 'models/reception';
import receptionService from 'services/reception.service';
import { ReceptionStatus, ReceptionType } from 'enums/reception.enum';

class Params {
    error: string = null;
    isLoaded: boolean = false;

    selectedYear: string = moment().format('YYYY');
    selectedMonth: string = "";

    monthlyRevenueData: Array<{ month: string; revenue: number }> = [];
    yearlyAsData: Array<{ year: string; count: number }> = [];
    yearlyDetailStats: {
        totalCount: number;
        normalCount: number;
        largeCount: number;
        freeRepairCount: number;
        totalRevenue: number;
        totalCost: number;
        tripCount: number;
    } = {
            totalCount: 0,
            normalCount: 0,
            largeCount: 0,
            freeRepairCount: 0,
            totalRevenue: 0,
            totalCost: 0,
            tripCount: 0
        };
    productTypeData: Array<{ productType: string; count: number }> = [];
    productTypeDetailStats: {
        totalCount: number;
        freeRepairCount: number;
        totalRevenue: number;
        totalCost: number;
        tripCount: number;
    } = {
            totalCount: 0,
            freeRepairCount: 0,
            totalRevenue: 0,
            totalCost: 0,
            tripCount: 0
        };
    regionAsData: Array<{ region: string; count: number }> = [];
    revenueDetailData: Array<{ month: string; estimate: number; tripFee: number; repairCost: number }> = [];
    largeBoilerData: Array<{ capacity: string; count: number }> = [];

    // ✅ 부품 데이터를 일반/대용량으로 분리
    normalPartData: Array<{ partName: string; count: number }> = [];
    heavyPartData: Array<{ partName: string; count: number }> = [];

    productTypeGraphData: Array<{ productType: string; count: number }> = [];
}

function AsAnalytics(props: any) {
    const [params, setParams] = useState(new Params());
    console.log('AsAnalytics params', params);

    /* ***************************************************************************************** */
    /* ************************************* STATE ********************************************* */
    /* ***************************************************************************************** */

    // 컴포넌트 마운트 시 데이터 로드
    useEffect(() => {
        loadAnalyticsData();
    }, [params.selectedYear, params.selectedMonth]); // selectedYear, selectedMonth 변경 시에도 다시 로드

    /* ***************************************************************************************** */
    /* ************************************* BASIC ********************************************* */
    /* ***************************************************************************************** */

    // AS 분석 데이터 로드 (실제 데이터만 사용)
    async function loadAnalyticsData() {
        try {
            setParams(prev => ({ ...prev, isLoaded: false }));

            // 1. 월별 AS수익 데이터 조회
            const monthlyData = await loadMonthlyRevenueData(params.selectedYear, params.selectedMonth);

            // 2. 연도별 AS현황 데이터 조회 
            const yearlyData = await loadYearlyAsData(params.selectedYear, params.selectedMonth);

            // 3. 종류별 AS현황 데이터 조회 (reception-item options 활용)
            const productTypeData = await loadProductTypeData(params.selectedYear, params.selectedMonth);

            // 4. 지역별 AS현황 데이터 조회
            const regionData = await loadRegionAsData(params.selectedYear, params.selectedMonth);

            // 5. 월별 수익 상세 데이터 조회 (견적금액, 출장비, 수리원가)
            const revenueDetailData = await loadRevenueDetailData(params.selectedYear, params.selectedMonth);

            // 6. 대용량보일러 상세현황 데이터 조회
            const largeBoilerData = await loadLargeBoilerData(params.selectedYear, params.selectedMonth);

            // 7. 부품상세현황 데이터 조회 (일반/대용량 구분)
            const partDetailData = await loadPartDetailData(params.selectedYear, params.selectedMonth);

            // 8. 종류별 AS현황 그래프용 (F, OB, YB, 대용량 분리)
            const productTypeGraphData = loadProductTypeGraphData(productTypeData, largeBoilerData);

            setParams(prev => ({
                ...prev,
                isLoaded: true,
                error: null,
                monthlyRevenueData: monthlyData,
                yearlyAsData: yearlyData,
                productTypeData: productTypeData,
                regionAsData: regionData,
                revenueDetailData: revenueDetailData,
                largeBoilerData: largeBoilerData,
                normalPartData: partDetailData.normalParts,  // ✅ 일반부품
                heavyPartData: partDetailData.heavyParts,    // ✅ 대용량부품
                productTypeGraphData: productTypeGraphData
            }));

        } catch (error) {
            setParams(prev => ({
                ...prev,
                isLoaded: true,
                error: error as any
            }));
            PageUtil.handleApiError(error);
        }
    }

    // 1. 월별 수익 데이터 로드
    async function loadMonthlyRevenueData(year: string, month: string): Promise<Array<{ month: string; revenue: number }>> {
        try {
            // 월 선택 여부에 따라 다른 데이터 조회
            const data = await receptionService.getMonthlyAsRevenue(
                parseInt(year),
                month ? parseInt(month) : 0
            );

            if (month) {
                // 월이 선택된 경우: 일별 데이터를 하나로 합산
                const totalRevenue = data.reduce((sum, item) => sum + item.revenue, 0);

                return [{
                    month: `${month}월`,
                    revenue: Math.round(totalRevenue / 10000) // 만원 단위로 변환
                }];
            } else {
                // 년도만 선택된 경우: 월별 데이터 반환
                return data.map(item => ({
                    month: `${item.month}월`,
                    revenue: Math.round(item.revenue / 10000) // 만원 단위로 변환
                }));
            }
        } catch (error) {
            console.error('월별 수익 데이터 로드 실패:', error);
            return [];
        }
    }

    // 2. 제조년월별 AS현황 데이터 로드
    async function loadYearlyAsData(year: string, month: string): Promise<Array<{ year: string; count: number }>> {
        try {
            const result = await receptionService.getYearlyAsStats(
                parseInt(year),
                month ? parseInt(month) : 0
            );

            // 상세 통계를 params에 저장
            setParams(prev => ({
                ...prev,
                yearlyDetailStats: result.detailStats
            }));

            return result.chartData.filter(item => item.count > 0);
        } catch (error) {
            console.error('제조년월별 AS현황 데이터 로드 실패:', error);
            return [];
        }
    }

    // 3. 종류별 AS현황 데이터 로드
    async function loadProductTypeData(year: string, month: string): Promise<Array<{ productType: string; count: number }>> {
        try {
            const result = await receptionService.getProductTypeAsStats(
                parseInt(year),
                month ? parseInt(month) : 0
            );

            // 상세 통계를 params에 저장
            setParams(prev => ({
                ...prev,
                productTypeDetailStats: result.detailStats
            }));

            return result.chartData.filter(item => item.count > 0);
        } catch (error) {
            console.error('종류별 AS현황 데이터 로드 실패:', error);
            return [];
        }
    }

    // 4. 지역별 AS현황 데이터 로드 (새로운 API 사용 - trip-fee 기준)
    async function loadRegionAsData(year: string, month: string): Promise<Array<{ region: string; count: number }>> {
        try {
            const data = await receptionService.getRegionAsStats(
                parseInt(year),
                month ? parseInt(month) : 0
            );

            return data
                .map(item => ({
                    region: item.province,
                    count: item.count
                }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 10); // 상위 10개 지역만
        } catch (error) {
            console.error('지역별 AS현황 데이터 로드 실패:', error);
            return [];
        }
    }

    // 5. 월별 수익 상세 데이터 로드 (견적금액, 출장비, 수리원가)
    async function loadRevenueDetailData(year: string, month: string): Promise<Array<{ month: string; estimate: number; tripFee: number; repairCost: number }>> {
        try {
            const data = await receptionService.getMonthlyRevenueDetail(
                parseInt(year),
                month ? parseInt(month) : 0
            );

            if (month) {
                // 월 선택 시: 일별 데이터를 하나로 합산
                const totalEstimate = data.reduce((sum, item) => sum + item.estimateAmount, 0);
                const totalTripFee = data.reduce((sum, item) => sum + item.tripFeeAmount, 0);
                const totalRepairCost = data.reduce((sum, item) => sum + item.repairCost, 0);

                return [{
                    month: `${month}월`,
                    estimate: Math.round(totalEstimate / 10000),      // 견적금액 (만원)
                    tripFee: Math.round(totalTripFee / 10000),        // 출장비 (만원)
                    repairCost: Math.round(totalRepairCost / 10000)   // 수리원가 (만원)
                }];
            } else {
                // 년도만 선택 시: 월별 데이터
                return data.map(item => ({
                    month: `${item.month}월`,
                    estimate: Math.round(item.estimateAmount / 10000),      // 견적금액 (만원)
                    tripFee: Math.round(item.tripFeeAmount / 10000),        // 출장비 (만원)
                    repairCost: Math.round(item.repairCost / 10000)         // 수리원가 (만원)
                }));
            }
        } catch (error) {
            console.error('월별 수익 상세 데이터 로드 실패:', error);
            return [];
        }
    }

    // 대용량보일러 데이터 로드 함수 추가
    async function loadLargeBoilerData(year: string, month: string): Promise<Array<{ capacity: string; count: number }>> {
        try {
            const data = await receptionService.getLargeBoilerStats(
                parseInt(year),
                month ? parseInt(month) : 0
            );
            return data;
        } catch (error) {
            console.error('대용량보일러 데이터 로드 실패:', error);
            return [];
        }
    }

    // 부품상세현황 데이터 로드 함수 추가
    async function loadPartDetailData(year: string, month: string): Promise<{
        normalParts: Array<{ partName: string; count: number }>;
        heavyParts: Array<{ partName: string; count: number }>;
    }> {
        try {
            const data = await receptionService.getPartDetailStats(
                parseInt(year),
                month ? parseInt(month) : 0
            );
            return data;
        } catch (error) {
            console.error('부품상세현황 데이터 로드 실패:', error);
            return { normalParts: [], heavyParts: [] };
        }
    }

    // 종류별 AS현황 그래프용 데이터 가공 (F, OB, YB, 대용량, 기타 분리)
    function loadProductTypeGraphData(
        productTypeData: Array<{ productType: string; count: number }>,
        largeBoilerData: Array<{ capacity: string; count: number }>
    ): Array<{ productType: string; count: number }> {
        // 1. productTypeData에서 F, OB, YB 그룹만 추출/합산
        const groups = { F: 0, OB: 0, YB: 0, 기타: 0 };

        productTypeData.forEach(item => {
            const productName = item.productType.toUpperCase();

            // 우선순위: F > OB > YB > 기타 (각 보일러를 하나의 그룹에만 배정)
            if (productName.indexOf('F') === 0 || productName.indexOf(' F') > -1) {
                // F 그룹 (F로 시작하거나 F를 포함)
                groups.F += item.count;
            } else if (productName.indexOf('OB') > -1) {
                // OB 그룹 (F가 아닌 경우에만)
                groups.OB += item.count;
            } else if (productName.indexOf('YB') > -1) {
                // YB 그룹 (F도 OB도 아닌 경우에만)
                groups.YB += item.count;
            } else {
                // 기타 그룹 (F, OB, YB가 아닌 모든 일반 보일러)
                groups.기타 += item.count;
            }
        });

        // 2. largeBoilerData 총합 계산 → 대용량
        const largeBoilerTotal = largeBoilerData.reduce((sum, item) => sum + item.count, 0);

        // 3. 최종 데이터 배열로 변환
        return [
            { productType: 'F', count: groups.F },
            { productType: 'OB', count: groups.OB },
            { productType: 'YB', count: groups.YB },
            { productType: '대용량', count: largeBoilerTotal },
            { productType: '기타', count: groups.기타 }
        ].filter(item => item.count > 0); // 0건인 그룹 제외
    }

    /* ***************************************************************************************** */
    /* ************************************** PAGE ********************************************* */
    /* ***************************************************************************************** */

    // 년도 변경 처리
    function handleYearChange(year: string) {
        setParams(prev => ({ ...prev, selectedYear: year }));
    }

    // 월 변경 처리
    function handleMonthChange(month: string) {
        setParams(prev => ({ ...prev, selectedMonth: month }));
    }

    // 데이터 새로고침
    function refreshData() {
        loadAnalyticsData();
    }

    /* ***************************************************************************************** */
    /* ************************************** VAC ********************************************** */
    /* ***************************************************************************************** */
    const args = {
        params: params,
        setParams: setParams,
        handleChange: (e: any, field: string, value: any) => {
            if (field === 'selectedYear') {
                handleYearChange(value);
            } else if (field === 'selectedMonth') {
                handleMonthChange(value);
            } else {
                PageUtil.handleChange(e, field, value, setParams);
            }
        },
        loadAnalyticsData: loadAnalyticsData,
        refreshData: refreshData,
    };

    return <AsAnalyticsV {...args}></AsAnalyticsV>;
}

export default withRouter(AsAnalytics);